import React, { useState, useEffect, useMemo } from 'react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight, Plus, Trash2, Pencil, ChevronsUpDown, KeyRound, CheckCircle2, AlertTriangle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MONTH_NAMES = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const DAY_LABELS = ['Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam', 'Dim'];

// Couleur par défaut (indigo, identique à l'ancien thème fixe) - utilisée
// pour tout événement sans couleur personnalisée choisie.
const DEFAULT_EVENT_COLOR = '#6366F1';
// Palette de couleurs proposées en un clic ; un sélecteur natif permet aussi
// de choisir n'importe quelle autre couleur.
const COLOR_PRESETS = ['#6366F1', '#EF4444', '#F59E0B', '#10B981', '#0EA5E9', '#EC4899', '#8B5CF6', '#64748B'];

// Date du jour (YYYY-MM-DD) en heure de Paris
export const getParisToday = () => {
  const parts = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris', year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(new Date()).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  return `${parts.year}-${parts.month}-${parts.day}`;
};

// Prévient les autres écrans qui affichent les rendez-vous du jour (tableau de
// bord, rappel de la main courante) pour qu'ils se rechargent sans avoir à
// actualiser la page.
export const CALENDAR_CHANGED_EVENT = 'calendar-events-changed';
const notifyCalendarChanged = () => window.dispatchEvent(new Event(CALENDAR_CHANGED_EVENT));

const CalendarWidget = ({ user, sites = [], initialMonth }) => {
  const todayStr = getParisToday();
  const [viewDate, setViewDate] = useState(() => {
    const base = initialMonth || todayStr;
    const [y, m] = base.split('-');
    return { year: parseInt(y, 10), month: parseInt(m, 10) - 1 };
  });
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [newTitle, setNewTitle] = useState('');
  const [newTime, setNewTime] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newSiteId, setNewSiteId] = useState('none');
  const [newVisibility, setNewVisibility] = useState('site');
  const [newRecurrence, setNewRecurrence] = useState('none');
  const [newRecurrenceEnd, setNewRecurrenceEnd] = useState('');
  const [newColor, setNewColor] = useState(DEFAULT_EVENT_COLOR);
  const [editingEventId, setEditingEventId] = useState(null);
  // Date de fin modifiable lors de l'édition d'un événement déjà sur
  // plusieurs jours consécutifs (ou pour lui en donner une pour la première
  // fois) - réservé aux chefs de poste/admins (voir canManage). null =
  // l'événement en cours d'édition ne peut pas se voir attribuer de fin
  // (occurrence d'une récurrence hebdomadaire/mensuelle non contiguë).
  const [editingEndDate, setEditingEndDate] = useState('');
  const [editingSpanEligible, setEditingSpanEligible] = useState(true);

  const canManage = user?.permission_overrides?.manage_calendar ?? ['admin', 'chef_poste', 'client', 'site'].includes(user?.role);
  // Un client ou un compte de site ne peut modifier/supprimer que les
  // événements qu'il a lui-même créés (vérifié aussi côté serveur) - jamais
  // ceux des agents/chef de poste ou d'un autre site.
  const canManageEvent = (ev) => canManage && (!['client', 'site'].includes(user?.role) || ev.created_by === user?.id);

  // Compte de site partagé : exige d'identifier le chef de poste rédacteur
  // par son code personnel avant de créer/modifier un événement, comme pour
  // la main courante (voir EventForm.js).
  const isSiteAccount = user?.role === 'site';
  const siteAccountSiteId = user?.assigned_sites?.[0];
  const [siteAgents, setSiteAgents] = useState([]);
  const [redacteurId, setRedacteurId] = useState('');
  const [redacteurPickerOpen, setRedacteurPickerOpen] = useState(false);
  const [redacteurPin, setRedacteurPin] = useState('');
  const [redacteurPinDialogOpen, setRedacteurPinDialogOpen] = useState(false);
  const [pinChecking, setPinChecking] = useState(false);
  const [pinVerified, setPinVerified] = useState(false);
  const [pinError, setPinError] = useState('');

  useEffect(() => {
    if (!isSiteAccount || !siteAccountSiteId) { setSiteAgents([]); return; }
    const token = localStorage.getItem('token');
    (async () => {
      try {
        const res = await fetch(`${BACKEND_URL}/api/sites/${siteAccountSiteId}/agents`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (res.ok) setSiteAgents(await res.json());
      } catch { /* silencieux */ }
    })();
    // eslint-disable-next-line
  }, [isSiteAccount, siteAccountSiteId]);

  const resetRedacteur = () => {
    setRedacteurId('');
    setRedacteurPin('');
    setPinVerified(false);
    setPinError('');
  };

  const verifyRedacteurPin = async (pin, agentId) => {
    setPinChecking(true);
    setPinError('');
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/calendar-events/verify-redacteur-pin`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ redacteur_id: agentId, pin })
      });
      if (res.ok) {
        setPinVerified(true);
        setRedacteurPinDialogOpen(false);
      } else {
        const err = await res.json();
        setPinError(err.detail || 'Code incorrect');
        setPinVerified(false);
        setRedacteurPin('');
      }
    } catch {
      setPinError('Erreur de connexion, merci de réessayer');
      setPinVerified(false);
    } finally {
      setPinChecking(false);
    }
  };

  useEffect(() => {
    loadEvents();
    // eslint-disable-next-line
  }, [viewDate]);

  const loadEvents = async () => {
    setLoading(true);
    const token = localStorage.getItem('token');
    const monthStr = `${viewDate.year}-${String(viewDate.month + 1).padStart(2, '0')}`;
    try {
      const res = await fetch(`${BACKEND_URL}/api/calendar-events?month=${monthStr}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) setEvents(await res.json());
    } catch { /* silencieux */ }
    finally { setLoading(false); }
  };

  // Toutes les occurrences d'un jour donné (utilisé par la fenêtre de détail
  // du jour, qui doit lister absolument tout, y compris les événements
  // affichés en trait continu dans la grille - voir chipEventsByDay).
  const eventsByDay = useMemo(() => {
    const map = {};
    events.forEach(ev => {
      if (!map[ev.date]) map[ev.date] = [];
      map[ev.date].push(ev);
    });
    return map;
  }, [events]);

  // Un événement "sur plusieurs jours consécutifs" (voir recurrence="daily")
  // regroupe plusieurs occurrences (une par jour) sous un même
  // recurrence_group_id, aux dates strictement consécutives - contrairement
  // à une récurrence hebdomadaire/mensuelle, dont les occurrences sont
  // espacées. C'est cette continuité qui permet de les distinguer sans que
  // le serveur ait besoin d'exposer le type de récurrence lui-même, et de
  // les afficher comme un seul trait continu (thème Google Agenda),
  // plutôt qu'une puce séparée sur chaque jour.
  const spanGroups = useMemo(() => {
    const byGroup = {};
    events.forEach(ev => {
      if (!ev.recurrence_group_id) return;
      (byGroup[ev.recurrence_group_id] = byGroup[ev.recurrence_group_id] || []).push(ev);
    });
    const spans = [];
    const spanEventIds = new Set();
    Object.values(byGroup).forEach((group) => {
      if (group.length < 2) return;
      const sorted = [...group].sort((a, b) => a.date.localeCompare(b.date));
      let contiguous = true;
      for (let i = 1; i < sorted.length; i++) {
        const prev = new Date(`${sorted[i - 1].date}T00:00:00`);
        const cur = new Date(`${sorted[i].date}T00:00:00`);
        if (Math.round((cur - prev) / 86400000) !== 1) { contiguous = false; break; }
      }
      if (!contiguous) return;
      sorted.forEach((e) => spanEventIds.add(e.id));
      spans.push({
        groupId: sorted[0].recurrence_group_id,
        title: sorted[0].title,
        color: sorted[0].color,
        startDate: sorted[0].date,
        endDate: sorted[sorted.length - 1].date,
        firstOccurrence: sorted[0],
      });
    });
    return { spans, spanEventIds };
  }, [events]);

  // Puces individuelles affichées dans chaque case du mois - exclut les
  // événements déjà représentés par un trait continu (spanGroups) pour ne
  // pas les afficher deux fois.
  const chipEventsByDay = useMemo(() => {
    const map = {};
    events.forEach(ev => {
      if (spanGroups.spanEventIds.has(ev.id)) return;
      (map[ev.date] = map[ev.date] || []).push(ev);
    });
    return map;
  }, [events, spanGroups]);

  const monthGrid = useMemo(() => {
    const firstOfMonth = new Date(viewDate.year, viewDate.month, 1);
    const daysInMonth = new Date(viewDate.year, viewDate.month + 1, 0).getDate();
    let startWeekday = firstOfMonth.getDay();
    startWeekday = startWeekday === 0 ? 6 : startWeekday - 1;

    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= daysInMonth; d++) {
      cells.push(`${viewDate.year}-${String(viewDate.month + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`);
    }
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [viewDate]);

  // Découpe la grille en semaines de 7 cases, pour calculer les segments de
  // trait continu semaine par semaine (un événement à cheval sur deux
  // semaines affiche un segment sur chacune, comme Google Agenda).
  const weeks = useMemo(() => {
    const result = [];
    for (let i = 0; i < monthGrid.length; i += 7) result.push(monthGrid.slice(i, i + 7));
    return result;
  }, [monthGrid]);

  // Pour une semaine donnée, calcule les segments de traits continus qui la
  // traversent (colonne de début/fin dans cette semaine) et leur attribue
  // une "voie" (lane) pour empiler sans chevaucher deux événements qui se
  // superposent - même principe qu'un diagramme de Gantt.
  const getWeekSpanLanes = (week) => {
    const segments = [];
    spanGroups.spans.forEach((span) => {
      let startCol = null;
      let endCol = null;
      week.forEach((dayStr, col) => {
        if (!dayStr) return;
        if (dayStr >= span.startDate && dayStr <= span.endDate) {
          if (startCol === null) startCol = col;
          endCol = col;
        }
      });
      if (startCol !== null) {
        segments.push({
          span,
          startCol,
          endCol,
          isTrueStart: week[startCol] === span.startDate,
          isTrueEnd: week[endCol] === span.endDate,
        });
      }
    });
    segments.sort((a, b) => a.startCol - b.startCol || (b.endCol - b.startCol) - (a.endCol - a.startCol));
    const laneEndCols = [];
    const withLane = segments.map((seg) => {
      let lane = laneEndCols.findIndex((endCol) => endCol < seg.startCol);
      if (lane === -1) { lane = laneEndCols.length; laneEndCols.push(seg.endCol); }
      else laneEndCols[lane] = seg.endCol;
      return { ...seg, lane };
    });
    return { segments: withLane, laneCount: laneEndCols.length };
  };

  const changeMonth = (offset) => {
    setViewDate(prev => {
      let month = prev.month + offset;
      let year = prev.year;
      if (month < 0) { month = 11; year -= 1; }
      if (month > 11) { month = 0; year += 1; }
      return { year, month };
    });
  };

  const openDay = (dayStr) => {
    setSelectedDay(dayStr);
    resetForm();
  };

  const resetForm = () => {
    setEditingEventId(null);
    setNewTitle('');
    setNewTime('');
    setNewDescription('');
    setNewSiteId('none');
    setNewVisibility('site');
    setNewRecurrence('none');
    setNewRecurrenceEnd('');
    setNewColor(DEFAULT_EVENT_COLOR);
    setEditingEndDate('');
    setEditingSpanEligible(true);
    resetRedacteur();
  };

  // Un événement déjà en trait continu (voir spanGroups) peut voir sa date de
  // fin modifiée ; un événement ponctuel (sans groupe) peut aussi s'en voir
  // attribuer une pour la première fois. Une occurrence d'une récurrence
  // hebdomadaire/mensuelle (groupe non contigu) n'est pas concernée.
  const getEditableSpanEndDate = (ev) => {
    if (!ev.recurrence_group_id) return ev.date;
    const span = spanGroups.spans.find((s) => s.groupId === ev.recurrence_group_id);
    return span ? span.endDate : null;
  };

  const startEditEvent = (ev) => {
    setEditingEventId(ev.id);
    setNewTitle(ev.title);
    setNewTime(ev.time || '');
    setNewDescription(ev.description || '');
    setNewSiteId(ev.site_id || 'none');
    setNewVisibility(ev.visibility || 'site');
    setNewColor(ev.color || DEFAULT_EVENT_COLOR);
    const spanEndDate = getEditableSpanEndDate(ev);
    setEditingSpanEligible(spanEndDate !== null);
    setEditingEndDate(spanEndDate || '');
    // Le code personnel n'est jamais conservé d'une modification à l'autre -
    // un compte de site doit systématiquement re-confirmer qui agit.
    resetRedacteur();
  };

  const handleSubmitEvent = async () => {
    if (!newTitle.trim() || !selectedDay) {
      toast.error('Donnez un titre au rendez-vous');
      return;
    }
    if (isSiteAccount && (!redacteurId || !pinVerified)) {
      toast.warning('Sélectionnez l\'agent rédacteur et confirmez son code personnel');
      return;
    }
    const isEditing = !!editingEventId;
    const payload = {
      title: newTitle.trim(),
      time: newTime || null,
      description: newDescription.trim() || null,
      date: selectedDay,
      site_id: newSiteId === 'none' ? null : newSiteId,
      visibility: newVisibility,
      color: newColor && newColor !== DEFAULT_EVENT_COLOR ? newColor : null,
      ...(isSiteAccount ? { redacteur_id: redacteurId, redacteur_pin: redacteurPin } : {}),
      ...((!isEditing && newRecurrence !== 'none') ? {
        recurrence: newRecurrence,
        recurrence_end_date: newRecurrenceEnd || null
      } : {}),
      ...(isEditing && editingEndDate ? { recurrence_end_date: editingEndDate } : {})
    };
    if (!isEditing && newRecurrence !== 'none' && !newRecurrenceEnd) {
      toast.error('Indiquez une date de fin pour la récurrence');
      return;
    }
    if (isEditing && editingEndDate && editingEndDate < selectedDay) {
      toast.error('La date de fin doit être après le jour de cet événement');
      return;
    }
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/calendar-events${isEditing ? `/${editingEventId}` : ''}`,
        {
          method: isEditing ? 'PUT' : 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }
      );
      if (res.ok) {
        toast.success(isEditing ? 'Rendez-vous modifié' : 'Ajouté au calendrier');
        resetForm();
        loadEvents();
        notifyCalendarChanged();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Erreur');
      }
    } catch {
      toast.error('Erreur de connexion');
    }
  };

  const handleDeleteEvent = async (ev) => {
    let deleteSeries = false;
    if (ev.recurrence_group_id) {
      deleteSeries = window.confirm(
        'Cet événement fait partie d\'une série récurrente. Cliquez sur "OK" pour supprimer TOUTE la série, ou sur "Annuler" pour ne supprimer que cette occurrence.'
      );
    }
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${BACKEND_URL}/api/calendar-events/${ev.id}${deleteSeries ? '?delete_series=true' : ''}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        toast.success(deleteSeries ? 'Série supprimée' : 'Supprimé');
        loadEvents();
        notifyCalendarChanged();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch {
      toast.error('Erreur de connexion');
    }
  };

  const getSiteName = (siteId) => sites.find(s => s.id === siteId)?.name;
  const selectedDayEvents = selectedDay ? (eventsByDay[selectedDay] || []) : [];

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-semibold text-slate-800 dark:text-slate-100">
          {MONTH_NAMES[viewDate.month]} {viewDate.year}
        </h3>
        <div className="flex gap-1">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => changeMonth(-1)} data-testid="calendar-prev-month">
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => changeMonth(1)} data-testid="calendar-next-month">
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-7 gap-1 mb-1">
            {DAY_LABELS.map(d => (
              <div key={d} className="text-center text-[11px] font-semibold text-slate-400 dark:text-slate-500 uppercase py-1">{d}</div>
            ))}
          </div>
          <div className="space-y-1">
            {weeks.map((week, weekIdx) => {
              const { segments, laneCount } = getWeekSpanLanes(week);
              const laneAreaHeight = laneCount * 18; // px, une voie = un trait continu
              return (
                <div key={weekIdx} className="relative">
                  <div className="grid grid-cols-7 gap-1">
                    {week.map((dayStr, col) => {
                      if (!dayStr) return <div key={`empty-${weekIdx}-${col}`} />;
                      const dayNum = parseInt(dayStr.split('-')[2], 10);
                      const dayEvents = chipEventsByDay[dayStr] || [];
                      const isToday = dayStr === todayStr;
                      const visibleEvents = dayEvents.slice(0, 2);
                      const remaining = dayEvents.length - visibleEvents.length;
                      return (
                        <button
                          key={dayStr}
                          type="button"
                          onClick={() => openDay(dayStr)}
                          className={`min-h-[72px] rounded-lg p-1 flex flex-col items-start text-left transition-colors hover:bg-slate-100 dark:hover:bg-slate-800 border ${
                            isToday ? 'border-indigo-400 bg-indigo-50 dark:bg-indigo-900/30' : 'border-transparent'
                          }`}
                          data-testid={`calendar-day-${dayStr}`}
                        >
                          <span className={`text-xs mb-0.5 ${isToday ? 'font-bold text-indigo-700 dark:text-indigo-300' : 'text-slate-600 dark:text-slate-300'}`}>{dayNum}</span>
                          {/* Espace réservé pour les traits continus superposés (voir overlay ci-dessous) */}
                          {laneAreaHeight > 0 && <div style={{ height: laneAreaHeight }} />}
                          <div className="w-full space-y-0.5">
                            {visibleEvents.map(ev => (
                              <div
                                key={ev.id}
                                className="w-full text-left text-[10px] leading-tight px-1 py-0.5 rounded text-white truncate"
                                style={{ backgroundColor: ev.color || DEFAULT_EVENT_COLOR }}
                                title={ev.title}
                              >
                                {ev.time ? `${ev.time} ` : ''}{ev.title}
                              </div>
                            ))}
                            {remaining > 0 && (
                              <div className="text-[10px] text-slate-400 pl-1">+{remaining} autre{remaining > 1 ? 's' : ''}</div>
                            )}
                          </div>
                        </button>
                      );
                    })}
                  </div>

                  {/* Traits continus (thème Google Agenda) : superposés à la
                      grille via la même disposition en grille (grid-cols-7),
                      pour un alignement pixel-parfait avec les cases du jour. */}
                  {segments.length > 0 && (
                    <div
                      className="absolute left-0 right-0 grid grid-cols-7 gap-1 pointer-events-none"
                      style={{ top: 20, gridAutoRows: 18, rowGap: 2 }}
                    >
                      {segments.map((seg) => (
                        <button
                          type="button"
                          key={`${seg.span.groupId}-${weekIdx}`}
                          onClick={() => openDay(week[seg.startCol])}
                          className={`pointer-events-auto flex items-center text-left text-[10px] leading-tight px-1.5 text-white truncate hover:brightness-90 ${
                            seg.isTrueStart ? 'rounded-l' : ''
                          } ${seg.isTrueEnd ? 'rounded-r' : ''}`}
                          style={{
                            gridColumn: `${seg.startCol + 1} / ${seg.endCol + 2}`,
                            gridRow: seg.lane + 1,
                            backgroundColor: seg.span.color || DEFAULT_EVENT_COLOR
                          }}
                          title={seg.span.title}
                          data-testid={`calendar-span-${seg.span.groupId}-${weekIdx}`}
                        >
                          {/* Le titre est répété en début de semaine pour un événement qui se
                              poursuit d'une semaine à l'autre, comme dans Google Agenda. */}
                          {(seg.isTrueStart || seg.startCol === 0) ? seg.span.title : ''}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      <Dialog open={!!selectedDay} onOpenChange={(open) => !open && setSelectedDay(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedDay && new Date(selectedDay + 'T12:00:00').toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-2">
            {selectedDayEvents.length === 0 ? (
              <p className="text-sm text-gray-400 dark:text-slate-500 text-center py-2">Aucun rendez-vous ce jour-là</p>
            ) : (
              selectedDayEvents.map(ev => (
                <div key={ev.id} className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 dark:text-slate-100 flex items-center gap-1.5">
                        <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ backgroundColor: ev.color || DEFAULT_EVENT_COLOR }} />
                        {ev.time && <span className="text-indigo-600 dark:text-indigo-400 font-semibold">{ev.time}</span>}
                        {ev.title}
                      </p>
                      {ev.description && <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{ev.description}</p>}
                      <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                        {ev.site_id && getSiteName(ev.site_id) && (
                          <Badge variant="outline" className="text-[10px]">{getSiteName(ev.site_id)}</Badge>
                        )}
                        {ev.visibility === 'chef_poste' && (
                          <Badge className="text-[10px] bg-amber-500 text-white">Chef de Poste uniquement</Badge>
                        )}
                        {ev.recurrence_group_id && (
                          <Badge variant="outline" className="text-[10px]">🔁 Fait partie d'une série</Badge>
                        )}
                        <span className="text-[10px] text-slate-400">Ajouté par {ev.redacteur_name || ev.created_by_name}</span>
                      </div>
                    </div>
                    {canManageEvent(ev) && (
                      <div className="flex gap-1 shrink-0">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => startEditEvent(ev)} data-testid={`edit-calendar-event-${ev.id}`}>
                          <Pencil className="h-3.5 w-3.5 text-slate-500" />
                        </Button>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleDeleteEvent(ev)} data-testid={`delete-calendar-event-${ev.id}`}>
                          <Trash2 className="h-3.5 w-3.5 text-red-500" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {canManage && (
            <div className="pt-3 border-t border-slate-200 dark:border-slate-700 space-y-2">
              <p className="text-xs font-medium text-slate-600 dark:text-slate-300">
                {editingEventId ? 'Modifier le rendez-vous' : 'Ajouter un rendez-vous / une mission'}
              </p>
              <Input
                placeholder="Titre (ex: Visite client, Formation SSIAP...)"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                data-testid="new-calendar-event-title"
              />

              {isSiteAccount && (
                <div className="space-y-1.5">
                  <label className="text-xs text-slate-500 dark:text-slate-400 block">Agent rédacteur *</label>
                  <Popover open={redacteurPickerOpen} onOpenChange={setRedacteurPickerOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        type="button"
                        variant="outline"
                        role="combobox"
                        className="w-full justify-between font-normal"
                        data-testid="calendar-redacteur-picker-trigger"
                      >
                        {siteAgents.find((a) => a.id === redacteurId)?.name || 'Sélectionnez un agent'}
                        <ChevronsUpDown className="h-4 w-4 opacity-50 shrink-0" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-[300px] p-0">
                      <Command>
                        <CommandInput placeholder="Rechercher un agent..." />
                        <CommandList>
                          <CommandEmpty>Aucun agent trouvé pour ce site.</CommandEmpty>
                          <CommandGroup>
                            {siteAgents.map((a) => (
                              <CommandItem
                                key={a.id}
                                value={a.name}
                                onSelect={() => {
                                  setRedacteurId(a.id);
                                  setRedacteurPin('');
                                  setPinVerified(false);
                                  setPinError('');
                                  setRedacteurPickerOpen(false);
                                  setRedacteurPinDialogOpen(true);
                                }}
                              >
                                {a.name}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Permet de savoir qui a réellement ajouté cet événement.</p>

                  {redacteurId && (
                    <div className="pt-0.5">
                      {pinVerified ? (
                        <div className="flex items-center gap-2 text-sm text-green-700 dark:text-green-400">
                          <CheckCircle2 className="h-4 w-4 shrink-0" />
                          <span>Code personnel confirmé</span>
                          <Button
                            type="button"
                            variant="link"
                            size="sm"
                            className="h-auto p-0 text-xs"
                            onClick={() => { setRedacteurPin(''); setPinVerified(false); setPinError(''); setRedacteurPinDialogOpen(true); }}
                            data-testid="calendar-redacteur-pin-edit"
                          >
                            Modifier
                          </Button>
                        </div>
                      ) : (
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          className="gap-2"
                          onClick={() => setRedacteurPinDialogOpen(true)}
                          data-testid="calendar-redacteur-pin-open-btn"
                        >
                          <KeyRound className="h-4 w-4" />
                          Saisir le code personnel de l'agent *
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="flex gap-2">
                <Input
                  type="time"
                  value={newTime}
                  onChange={(e) => setNewTime(e.target.value)}
                  className="w-32"
                  title="Heure (facultatif)"
                  data-testid="new-calendar-event-time"
                />
                {sites.length > 0 && (
                  <Select value={newSiteId} onValueChange={setNewSiteId}>
                    <SelectTrigger className="flex-1"><SelectValue placeholder="Site concerné (facultatif)" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun site en particulier</SelectItem>
                      {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>
              <Textarea
                placeholder="Description (facultatif)"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                rows={2}
              />
              <div>
                <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Couleur</label>
                <div className="flex items-center gap-1.5 flex-wrap">
                  {COLOR_PRESETS.map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setNewColor(c)}
                      className={`h-6 w-6 rounded-full border-2 ${newColor === c ? 'border-slate-800 dark:border-white' : 'border-transparent'}`}
                      style={{ backgroundColor: c }}
                      title={c}
                      data-testid={`calendar-color-${c}`}
                    />
                  ))}
                  <input
                    type="color"
                    value={newColor}
                    onChange={(e) => setNewColor(e.target.value)}
                    className="h-6 w-8 rounded border border-slate-300 dark:border-slate-600 cursor-pointer bg-transparent"
                    title="Couleur personnalisée"
                    data-testid="new-calendar-event-color"
                  />
                </div>
              </div>
              {editingEventId && editingSpanEligible && (
                <div>
                  <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">
                    Se termine le (dernier jour inclus, pour un événement sur plusieurs jours)
                  </label>
                  <Input
                    type="date"
                    min={selectedDay}
                    value={editingEndDate}
                    onChange={(e) => setEditingEndDate(e.target.value)}
                    data-testid="edit-calendar-event-end-date"
                  />
                </div>
              )}
              {user?.role !== 'client' && (
                <div>
                  <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Visible par</label>
                  <Select value={newVisibility} onValueChange={setNewVisibility}>
                    <SelectTrigger data-testid="new-calendar-event-visibility"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="site">Tout le site (chef de poste, agents, clients)</SelectItem>
                      <SelectItem value="chef_poste">Chef de Poste uniquement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
              {!editingEventId && (
                <div>
                  <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">Durée / récurrence</label>
                  <Select value={newRecurrence} onValueChange={setNewRecurrence}>
                    <SelectTrigger data-testid="new-calendar-event-recurrence"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Un seul jour</SelectItem>
                      <SelectItem value="daily">Sur plusieurs jours consécutifs</SelectItem>
                      <SelectItem value="weekly">Toutes les semaines, ce jour-là</SelectItem>
                      <SelectItem value="monthly">Tous les mois, cette date-là</SelectItem>
                    </SelectContent>
                  </Select>
                  {newRecurrence !== 'none' && (
                    <div className="mt-2">
                      <label className="text-xs text-slate-500 dark:text-slate-400 mb-1 block">
                        {newRecurrence === 'daily' ? "Jusqu'au (dernier jour inclus)" : "Jusqu'au"}
                      </label>
                      <Input
                        type="date"
                        min={selectedDay}
                        value={newRecurrenceEnd}
                        onChange={(e) => setNewRecurrenceEnd(e.target.value)}
                        data-testid="new-calendar-event-recurrence-end"
                      />
                    </div>
                  )}
                </div>
              )}
              <div className="flex gap-2">
                {editingEventId && (
                  <Button variant="outline" className="flex-1" onClick={resetForm}>
                    Annuler
                  </Button>
                )}
                <Button className="flex-1 btn-primary" onClick={handleSubmitEvent} data-testid="add-calendar-event-btn">
                  {editingEventId ? (
                    'Enregistrer les modifications'
                  ) : (
                    <>
                      <Plus className="h-4 w-4 mr-2" />
                      Ajouter
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Popup de saisie du code personnel du rédacteur : dès les 4 chiffres
          renseignés, le code est vérifié immédiatement auprès du serveur -
          elle ne se ferme que si le code est le bon. */}
      <Dialog open={redacteurPinDialogOpen} onOpenChange={(open) => { if (!pinChecking) setRedacteurPinDialogOpen(open); }}>
        <DialogContent className="max-w-xs">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-base">
              <KeyRound className="h-4 w-4 text-indigo-600" />
              Code personnel
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-gray-500 dark:text-slate-400">
            Demandé à {siteAgents.find((a) => a.id === redacteurId)?.name || 'l\'agent sélectionné'} pour confirmer son identité.
          </p>
          <div className="flex flex-col items-center gap-2 py-2">
            <InputOTP
              maxLength={4}
              inputMode="numeric"
              value={redacteurPin}
              onChange={(value) => { setRedacteurPin(value); setPinError(''); }}
              onComplete={(value) => verifyRedacteurPin(value, redacteurId)}
              disabled={pinChecking}
              data-testid="calendar-redacteur-pin-otp"
              autoFocus
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} mask />
                <InputOTPSlot index={1} mask />
                <InputOTPSlot index={2} mask />
                <InputOTPSlot index={3} mask />
              </InputOTPGroup>
            </InputOTP>

            {pinChecking && (
              <p className="text-xs text-slate-500 dark:text-slate-400">Vérification...</p>
            )}

            {pinError && (
              <div
                className="flex items-start gap-2 p-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900 text-red-700 dark:text-red-300 text-xs w-full"
                data-testid="calendar-redacteur-pin-error"
              >
                <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                <span>{pinError}</span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CalendarWidget;
